import { debounce } from "es-toolkit";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo } from "react";

type UseMediaVolumeCommitOptions = {
  mediaVolume: number;
  setMediaVolume: Dispatch<SetStateAction<number>>;
};

type CommitMediaVolumeOptions = {
  immediate?: boolean;
};

export const useMediaVolumeCommit = ({
  mediaVolume,
  setMediaVolume,
}: UseMediaVolumeCommitOptions) => {
  const debouncedSetMediaVolume = useMemo(
    () => debounce((volume: number) => setMediaVolume(volume), 150),
    [setMediaVolume],
  );

  useEffect(() => {
    return () => {
      debouncedSetMediaVolume.flush();
    };
  }, [debouncedSetMediaVolume]);

  return useCallback(
    (volume: number, options: CommitMediaVolumeOptions = {}) => {
      if (options.immediate || volume <= 0 || mediaVolume <= 0) {
        debouncedSetMediaVolume.cancel();
        setMediaVolume(volume);
        return;
      }

      debouncedSetMediaVolume(volume);
    },
    [debouncedSetMediaVolume, mediaVolume, setMediaVolume],
  );
};
